import React, { useEffect, useState } from 'react'
import {
    WemoloButton,
    WemoloCalendarIcon,
    WemoloDownloadIcon,
    WemoloImageIcon,
    WemoloLicensePlate,
    WemoloModal,
    WemoloScannerIcon,
    WemoloTypography
} from '../../../components/Wemolo'
import { DETECTION_EXPLORER_KNOWN_COUNTRIES, DETECTION_EXPLORER_TEXT, DetectionExplorerKnownCountry } from '../config'
import { formatDetectionTimestamp } from '../formatTimestamp'
import { DetectionResult } from '../get_detections'
import { CategoryBadge } from './CategoryBadge'
import { DirectionChip, VehicleTypeChip } from './Chips'
import { CopyButton } from './CopyButton'
import styles from './DetectionDetailModal.module.css'

type DetectionImageTab = 'detection' | 'context' | 'plate'

export type DetectionDetailModalProps = {
    detection: DetectionResult
    onClose: () => void
}

const CONFIDENCE_THRESHOLD = 0.5

const resolveCountry = (raw: string | null): DetectionExplorerKnownCountry =>
    DETECTION_EXPLORER_KNOWN_COUNTRIES.includes(raw as DetectionExplorerKnownCountry)
        ? (raw as DetectionExplorerKnownCountry)
        : 'INT'

const formatConfidence = (value: number | null): string =>
    value === null ? DETECTION_EXPLORER_TEXT.emptyValue : value.toFixed(3)

const TAB_ORDER: ReadonlyArray<{ id: DetectionImageTab; label: string; title: string }> = [
    {
        id: 'detection',
        label: DETECTION_EXPLORER_TEXT.detail.tabRender,
        title: DETECTION_EXPLORER_TEXT.detail.tabRenderHint
    },
    {
        id: 'context',
        label: DETECTION_EXPLORER_TEXT.detail.tabContext,
        title: DETECTION_EXPLORER_TEXT.detail.tabContextHint
    },
    { id: 'plate', label: DETECTION_EXPLORER_TEXT.detail.tabPlate, title: DETECTION_EXPLORER_TEXT.detail.tabPlateHint }
]

const imageUrlForTab = (detection: DetectionResult, tab: DetectionImageTab): string | null => {
    if (tab === 'detection') return detection.images.render.url
    if (tab === 'plate') return detection.images.lp.url
    return detection.images.roi.url
}

const extensionForType = (type: string): string => {
    if (type === 'image/png') return 'png'
    if (type === 'image/webp') return 'webp'
    return 'jpg'
}

const downloadImage = async (url: string, fileBase: string): Promise<void> => {
    const response = await fetch(url)
    if (!response.ok) throw new Error(`Image download failed: ${response.status}`)
    const blob = await response.blob()
    const objectUrl = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = `${fileBase}.${extensionForType(blob.type)}`
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(objectUrl)
}

const placeholderForTab = (tab: DetectionImageTab): string => {
    if (tab === 'context') return DETECTION_EXPLORER_TEXT.detail.contextUnavailable
    if (tab === 'plate') return DETECTION_EXPLORER_TEXT.detail.plateUnavailable
    return DETECTION_EXPLORER_TEXT.onDevice
}

export const DetectionDetailModal: React.FC<DetectionDetailModalProps> = ({ detection, onClose }) => {
    const [activeTab, setActiveTab] = useState<DetectionImageTab>('detection')
    const [isDownloading, setIsDownloading] = useState(false)
    const detectionUuid = detection.detection_uuid

    useEffect(() => {
        setActiveTab('detection')
    }, [detectionUuid])

    const country = resolveCountry(detection.country)
    const lprConf = detection.lpr_conf
    const lpness = detection.lpness
    const lprLow = lprConf !== null && lprConf < CONFIDENCE_THRESHOLD
    const lpnessLow = lpness !== null && lpness < CONFIDENCE_THRESHOLD

    const imageUrl = imageUrlForTab(detection, activeTab)

    const handleDownload = async () => {
        if (!imageUrl || isDownloading) return
        setIsDownloading(true)
        try {
            await downloadImage(imageUrl, `${detection.detection_uuid}_${activeTab}`)
        } catch (error) {
            // The signed S3 URL is cross-origin; without CORS on the bucket the blob fetch is blocked.
            // Fall back to opening the image in a new tab so the user can still save it.
            // eslint-disable-next-line no-console
            console.warn('[DetectionDetailModal] blob download blocked (S3 CORS?), opening in new tab:', error)
            window.open(imageUrl, '_blank', 'noopener,noreferrer')
        } finally {
            setIsDownloading(false)
        }
    }

    return (
        <WemoloModal isOpen onClose={onClose} size="fullscreen" bodyPadding="none">
            <div className={styles.layout}>
                <div className={styles.imagePane}>
                    <div className={`${styles.imageStage} ${activeTab === 'plate' ? styles.imageStagePlate : ''}`}>
                        {imageUrl ? (
                            <img
                                src={imageUrl}
                                alt={
                                    activeTab === 'plate'
                                        ? 'License plate crop'
                                        : activeTab === 'context'
                                          ? 'Region of interest'
                                          : 'Render'
                                }
                                className={activeTab === 'plate' ? styles.imageNatural : styles.image}
                            />
                        ) : (
                            <div className={styles.imagePlaceholder}>
                                <WemoloImageIcon />
                                <WemoloTypography variant="body-small" tag="p">
                                    {placeholderForTab(activeTab)}
                                </WemoloTypography>
                            </div>
                        )}
                    </div>
                    <div className={styles.tabBarRow}>
                        <div className={styles.tabBar} role="tablist">
                            {TAB_ORDER.map((tab) => (
                                <span key={tab.id} className={styles.tabItem} title={tab.title}>
                                    <WemoloButton
                                        view={activeTab === tab.id ? 'primary' : 'ghost'}
                                        size="small"
                                        onClick={() => setActiveTab(tab.id)}
                                    >
                                        {tab.label}
                                    </WemoloButton>
                                </span>
                            ))}
                        </div>
                        <WemoloButton
                            view="secondary"
                            size="small"
                            className={styles.downloadButton}
                            icon={<WemoloDownloadIcon />}
                            disabled={!imageUrl || isDownloading}
                            onClick={handleDownload}
                        >
                            {isDownloading
                                ? DETECTION_EXPLORER_TEXT.detail.downloading
                                : DETECTION_EXPLORER_TEXT.detail.download}
                        </WemoloButton>
                    </div>
                </div>

                <div className={styles.sidePane}>
                    <div className={styles.identityBar}>
                        <div className={styles.identityLeft}>
                            <WemoloLicensePlate
                                plateNumber={detection.license_plate ?? DETECTION_EXPLORER_TEXT.emptyValue}
                                size="medium"
                                country={country}
                            />
                            <div className={styles.identityChips}>
                                <CategoryBadge category={detection.category} />
                                <DirectionChip direction={detection.direction} />
                                <VehicleTypeChip vehicleType={detection.vehicle_type} />
                            </div>
                        </div>
                        <div className={styles.identityMeta}>
                            <div className={styles.identityMetaItem} title={DETECTION_EXPLORER_TEXT.detail.deviceId}>
                                <WemoloScannerIcon />
                                <WemoloTypography variant="body-small" tag="span">
                                    {detection.device_id}
                                </WemoloTypography>
                            </div>
                            <div className={styles.identityMetaItem}>
                                <WemoloCalendarIcon />
                                <WemoloTypography variant="body-small" tag="span">
                                    {formatDetectionTimestamp(detection.timestamp)}
                                </WemoloTypography>
                            </div>
                        </div>
                    </div>

                    <div className={styles.detailsRow}>
                        <div className={styles.section}>
                            <WemoloTypography variant="label-small" tag="span" className={styles.sectionTitle}>
                                {DETECTION_EXPLORER_TEXT.detail.recognitionConfidence}
                            </WemoloTypography>
                            <div className={styles.confidenceRow}>
                                <div className={styles.confidenceHeader}>
                                    <WemoloTypography
                                        variant="body-small"
                                        tag="span"
                                        className={styles.confidenceLabel}
                                    >
                                        {DETECTION_EXPLORER_TEXT.detail.lprConfidence}
                                    </WemoloTypography>
                                    <WemoloTypography
                                        variant="body-small"
                                        tag="span"
                                        className={lprLow ? styles.confidenceValueLow : styles.confidenceValueOk}
                                    >
                                        {formatConfidence(lprConf)}
                                    </WemoloTypography>
                                </div>
                                <div className={styles.confidenceBar}>
                                    <div
                                        className={`${styles.confidenceBarFill} ${
                                            lprLow ? styles.confidenceBarFillLow : styles.confidenceBarFillOk
                                        }`}
                                        style={{ width: `${Math.max(0, Math.min(1, lprConf ?? 0)) * 100}%` }}
                                    />
                                </div>
                                {lprLow && (
                                    <WemoloTypography
                                        variant="label-small"
                                        tag="span"
                                        className={styles.confidenceHint}
                                    >
                                        {DETECTION_EXPLORER_TEXT.detail.lprBelowThreshold}
                                    </WemoloTypography>
                                )}
                            </div>
                            <div className={styles.confidenceRow}>
                                <div className={styles.confidenceHeader}>
                                    <WemoloTypography
                                        variant="body-small"
                                        tag="span"
                                        className={styles.confidenceLabel}
                                    >
                                        {DETECTION_EXPLORER_TEXT.detail.lpness}
                                    </WemoloTypography>
                                    <WemoloTypography
                                        variant="body-small"
                                        tag="span"
                                        className={lpnessLow ? styles.confidenceValueLow : styles.confidenceValueOk}
                                    >
                                        {formatConfidence(lpness)}
                                    </WemoloTypography>
                                </div>
                                <div className={styles.confidenceBar}>
                                    <div
                                        className={`${styles.confidenceBarFill} ${
                                            lpnessLow ? styles.confidenceBarFillLow : styles.confidenceBarFillOk
                                        }`}
                                        style={{ width: `${Math.max(0, Math.min(1, lpness ?? 0)) * 100}%` }}
                                    />
                                </div>
                                <WemoloTypography variant="label-small" tag="span" className={styles.confidenceHint}>
                                    {DETECTION_EXPLORER_TEXT.detail.lpnessHint}
                                </WemoloTypography>
                            </div>
                        </div>

                        <div className={styles.sectionDivider} aria-hidden="true" />

                        <div className={styles.section}>
                            <WemoloTypography variant="label-small" tag="span" className={styles.sectionTitle}>
                                {DETECTION_EXPLORER_TEXT.detail.detection}
                            </WemoloTypography>
                            <div className={styles.metadataGrid}>
                                <WemoloTypography variant="body-small" tag="span" className={styles.metadataLabel}>
                                    {DETECTION_EXPLORER_TEXT.detail.detectionId}
                                </WemoloTypography>
                                <div className={styles.metadataValueGroup}>
                                    <WemoloTypography
                                        variant="label-small"
                                        tag="span"
                                        className={styles.metadataValueMono}
                                    >
                                        {detection.detection_uuid}
                                    </WemoloTypography>
                                    <CopyButton
                                        value={detection.detection_uuid}
                                        ariaLabel={DETECTION_EXPLORER_TEXT.detail.copyDetectionId}
                                    />
                                </div>
                                <WemoloTypography variant="body-small" tag="span" className={styles.metadataLabel}>
                                    {DETECTION_EXPLORER_TEXT.detail.deviceUuid}
                                </WemoloTypography>
                                <div className={styles.metadataValueGroup}>
                                    <WemoloTypography
                                        variant="label-small"
                                        tag="span"
                                        className={styles.metadataValueMono}
                                    >
                                        {detection.device_uuid}
                                    </WemoloTypography>
                                    <CopyButton
                                        value={detection.device_uuid}
                                        ariaLabel={DETECTION_EXPLORER_TEXT.detail.copyDeviceUuid}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </WemoloModal>
    )
}
